import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { User } from "~/types/user";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isObjectID(id: string) {
  return id.length === 24 && /^[0-9a-fA-F]+$/.test(id);
}

// ============================================================================
// Currency Support
// ============================================================================

/**
 * Mapping of currency codes to their symbols
 */
export const CURRENCY_SYMBOLS = {
  USD: "$",
  CAD: "C$",
  AUD: "A$",
  GBP: "£",
  EUR: "€",
} as const;

export type SupportedCurrency = keyof typeof CURRENCY_SYMBOLS;

/**
 * Get currency symbol for a given currency code
 * @param currency - Currency code (e.g., "USD", "EUR")
 * @returns Currency symbol (e.g., "$", "€")
 */
export function getCurrencySymbol(currency: string): string {
  const upperCurrency = currency.toUpperCase() as SupportedCurrency;
  return CURRENCY_SYMBOLS[upperCurrency] || CURRENCY_SYMBOLS.USD;
}

/**
 * Get user's currency from user data, with fallback to USD
 * Handles both User type and any type for backward compatibility
 * @param user - User object that may contain currency field
 * @returns Currency code (e.g., "USD", "EUR")
 */
export function getUserCurrency(user: User | null | undefined): string {
  if (!user) return "USD";
  return user.currency || "USD";
}

/**
 * Format price in cents with currency symbol
 * @param cents - Price in cents
 * @param currency - Currency code (defaults to "USD")
 * @returns Formatted price string (e.g., "$12.34", "€45.67")
 */
export function formatPriceCents(cents: number, currency = "USD"): string {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${(cents / 100).toFixed(2)}`;
}

/**
 * Format price in cents with user's currency
 * @param cents - Price in cents
 * @param user - User object that may contain currency field
 * @returns Formatted price string with user's currency symbol
 */
export function formatPriceCentsForUser(cents: number, user: User | null | undefined): string {
  const currency = getUserCurrency(user);
  return formatPriceCents(cents, currency);
}
